import { useState } from 'preact/hooks';
import type {
  BookingDraft,
  BookingGuest,
  ChatReply,
} from '../shared/contracts';
import { formatPrice, formatDuration, formatDisplayDate } from './format';
import { getWidgetCopy } from './i18n';
import { CheckIcon } from './icons';

interface Props {
  draft: BookingDraft;
  disabled: boolean;
  locale: string;
  onSubmit: (guest: BookingGuest) => Promise<ChatReply>;
}

const initialGuest: BookingGuest = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  note: '',
  privacyAccepted: false,
};

export function BookingForm({
  draft,
  disabled,
  locale,
  onSubmit,
}: Props) {
  const copy = getWidgetCopy(locale);
  const [guest, setGuest] = useState(initialGuest);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [completed, setCompleted] = useState(false);

  const update = (field: keyof BookingGuest, value: string | boolean) => {
    setGuest((current) => ({ ...current, [field]: value }));
    setError('');
  };

  const submit = async (event: Event) => {
    event.preventDefault();
    setSubmitted(true);
    if (
      !guest.firstName.trim() ||
      !guest.lastName.trim() ||
      !guest.email.trim() ||
      !guest.phone.trim() ||
      !guest.privacyAccepted
    ) {
      setError(copy.requiredFields);
      return;
    }
    try {
      await onSubmit(guest);
      setCompleted(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.bookingFailed);
    }
  };

  return (
    <form class="bc-booking-form" onSubmit={submit}>
      <div class="bc-booking-summary">
        <div class="bc-summary-top">
          <div>
            <span class="bc-kicker">{copy.yourAppointment}</span>
            <strong>{draft.slot.serviceName}</strong>
          </div>
          <span class="bc-summary-price">
            {formatPrice(draft.slot.price, draft.currency, locale)}
          </span>
        </div>
        <div class="bc-summary-details">
          <span>{formatDisplayDate(draft.slot.date, locale)}</span>
          <i />
          <span>{draft.slot.time}</span>
          <i />
          <span>{draft.slot.masterName}</span>
        </div>
        <span class="bc-summary-duration">
          {formatDuration(draft.slot.durationMinutes, locale)}
        </span>
      </div>

      <div class="bc-form-grid">
        <label>
          <span>{copy.firstName} *</span>
          <input
            value={guest.firstName}
            onInput={(event) => update('firstName', event.currentTarget.value)}
            class={submitted && !guest.firstName.trim() ? 'bc-invalid' : ''}
            autocomplete="given-name"
            maxlength={120}
            required
            aria-invalid={submitted && !guest.firstName.trim()}
          />
        </label>
        <label>
          <span>{copy.lastName} *</span>
          <input
            value={guest.lastName}
            onInput={(event) => update('lastName', event.currentTarget.value)}
            class={submitted && !guest.lastName.trim() ? 'bc-invalid' : ''}
            autocomplete="family-name"
            maxlength={120}
            required
            aria-invalid={submitted && !guest.lastName.trim()}
          />
        </label>
      </div>
      <label>
        <span>{copy.email} *</span>
        <input
          type="email"
          value={guest.email}
          onInput={(event) => update('email', event.currentTarget.value)}
          class={submitted && !guest.email.trim() ? 'bc-invalid' : ''}
          autocomplete="email"
          maxlength={255}
          required
          aria-invalid={submitted && !guest.email.trim()}
        />
      </label>
      <label>
        <span>{copy.phone} *</span>
        <input
          type="tel"
          value={guest.phone}
          onInput={(event) => update('phone', event.currentTarget.value)}
          class={submitted && !guest.phone.trim() ? 'bc-invalid' : ''}
          autocomplete="tel"
          maxlength={255}
          required
          aria-invalid={submitted && !guest.phone.trim()}
        />
      </label>
      <label>
        <span>{copy.salonNote}</span>
        <textarea
          rows={2}
          value={guest.note}
          onInput={(event) => update('note', event.currentTarget.value)}
          placeholder={copy.optional}
          maxlength={2000}
        />
      </label>

      <button
        class={`bc-consent ${guest.privacyAccepted ? 'bc-consent-checked' : ''}`}
        type="button"
        onClick={() => update('privacyAccepted', !guest.privacyAccepted)}
        aria-pressed={guest.privacyAccepted}
      >
        <span class="bc-checkbox">{guest.privacyAccepted && <CheckIcon />}</span>
        <span>{copy.privacyConsent}</span>
      </button>

      {error && <div class="bc-form-error" role="alert">{error}</div>}
      <button class="bc-book-button" type="submit" disabled={disabled || completed}>
        {completed
          ? copy.bookingSubmitted
          : disabled
            ? copy.confirming
            : copy.confirmBooking}
      </button>
      <span class="bc-pay-note">{copy.paymentAtSalon}</span>
    </form>
  );
}
